import { CheckIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Alert,
  Autocomplete,
  Box,
  Button,
  Group,
  List,
  rem,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme
} from "@mantine/core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattableTextArea } from "../../../../../components/common/FormattableTextArea";
import { MonthYearInput, validateMonthYear } from "../../../../../components/common/MonthYearInput";
import { TechnologySelector } from "../../../../../components/common/TechnologySelector";
import { FormUIColor } from "../../../../../constants/colorPalette";
import { useInternalCustomersDictionary } from "../../../../../hooks/queries/useInternalCustomersDictionary";
import { useRolesDictionary } from "../../../../../hooks/queries/useRolesDictionary";
import { useTechnologiesDictionary } from "../../../../../hooks/queries/useTechnologiesDictionary";
import { TechnologyService } from "../../../../../services/technologyService";
import { normalizeYearInput, toUTCDate } from "../../../../../utils/dateFormat";
import {
  ProjectExperienceFormData,
  TechnologyWithFavorite,
} from "../models/projectExperience.model";

// Export the form data type
export type { ProjectExperienceFormData };

interface ProjectExperienceFormProps {
  project: ProjectExperienceFormData;
  onChange: (project: ProjectExperienceFormData) => void;
  onSave: (project: ProjectExperienceFormData) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

export function ProjectExperienceForm({
  project,
  onChange,
  onSave,
  onCancel,
  isEditing = false,
}: ProjectExperienceFormProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const { data: technologiesDictionary, isLoading: loadingTechnologies } =
    useTechnologiesDictionary();
  const { data: rolesDictionaryData, isLoading: loadingRoles } = useRolesDictionary();
  const rolesDictionary = rolesDictionaryData?.filter((r): r is string => !!r) ?? [];
  const { data: customersDictionaryData } = useInternalCustomersDictionary();
  const customersDictionary = customersDictionaryData ?? [];

  // Local state for form fields to improve performance
  const [localProject, setLocalProject] =
    useState<ProjectExperienceFormData>(project);
  const localProjectRef = useRef(localProject);
  localProjectRef.current = localProject;
  const [responsibilitiesText, setResponsibilitiesText] = useState(
    project.responsibilities.join("\n")
  );

  // Convert technologies to TechnologyWithFavorite format
  const [technologiesWithFavorites, setTechnologiesWithFavorites] = useState<
    TechnologyWithFavorite[]
  >([]);

  // Validation state
  const [showValidation, setShowValidation] = useState(false);

  const technologyService = new TechnologyService();

  // Debounce responsibilities updates to improve performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Update local project and parent with debounced responsibilities
      const responsibilitiesArray = responsibilitiesText.split("\n");
      const updated = { ...localProjectRef.current, responsibilities: responsibilitiesArray };
      setLocalProject(updated);
      onChange(updated);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [responsibilitiesText, onChange]);

  // Validation functions
  const validateStartDate = () => {
    return validateMonthYear(localProject.fromMonth, localProject.fromYear, true);
  };

  const validateEndDate = () => {
    return validateMonthYear(localProject.toMonth, localProject.toYear, false);
  };

  const validateDateRange = () => {
    // Only validate if both dates are provided
    if (localProject.fromMonth && localProject.fromYear &&
      localProject.toMonth && localProject.toYear) {
      const normalizedFromYear = normalizeYearInput(localProject.fromYear);
      const normalizedToYear = normalizeYearInput(localProject.toYear);

      // Use UTC to avoid timezone issues in date comparison
      const startDate = toUTCDate(new Date(parseInt(normalizedFromYear), parseInt(localProject.fromMonth) - 1, 1));
      const endDate = toUTCDate(new Date(parseInt(normalizedToYear), parseInt(localProject.toMonth) - 1, 1));

      if (endDate < startDate) {
        return "End date must be after start date";
      }
    }
    return null;
  };

  const isFormValid =
    localProject.customerName &&
    localProject.projectName &&
    localProject.fromMonth &&
    localProject.fromYear &&
    responsibilitiesText.trim() !== "" &&
    technologiesWithFavorites.length > 0 &&
    !validateStartDate() &&
    !validateEndDate() &&
    !validateDateRange();

  // Convert project technologies to TechnologyWithFavorite format when technologies dictionary is loaded
  const convertProjectTechnologies = useCallback(
    (projectTechs: any[], techDict: any[]) => {
      return (
        projectTechs?.map((tech) => {
          if (typeof tech === "string") {
            // Legacy string format - find ID from dictionary
            const foundTech = techDict.find((t) => t.name === tech);
            return {
              id: foundTech?.id || 0,
              name: tech,
              isFavorite: false,
            };
          } else {
            // New format with name and isFavorite, need to find ID
            const foundTech = techDict.find((t) => t.name === tech.name);
            return {
              id: foundTech?.id || tech.id || 0,
              name: tech.name,
              isFavorite: tech.isFavorite || false,
            };
          }
        }) || []
      );
    },
    []
  );

  // Update local state when project prop changes from outside
  useEffect(() => {
    setLocalProject(project);
    setResponsibilitiesText(project.responsibilities.join("\n"));
    // Reset validation when project changes
    setShowValidation(false);

    // Only convert technologies if we have the dictionary loaded
    if (technologiesDictionary && technologiesDictionary.length > 0) {
      setTechnologiesWithFavorites(
        convertProjectTechnologies(project.technologies, technologiesDictionary)
      );
    }
  }, [project, technologiesDictionary, convertProjectTechnologies]);

  // Debounced update to parent component
  const updateParent = useCallback(
    (updatedProject: ProjectExperienceFormData) => {
      onChange(updatedProject);
    },
    [onChange]
  );

  // Update local project state
  const updateLocalProject = useCallback(
    (updates: Partial<ProjectExperienceFormData>) => {
      setLocalProject((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  // Convert existing project technologies once dictionary is loaded
  useEffect(() => {
    if (technologiesDictionary && !loadingTechnologies) {
      if (project.technologies) {
        setTechnologiesWithFavorites(
          convertProjectTechnologies(
            project.technologies,
            technologiesDictionary
          )
        );
      }
    }
  }, [
    project.technologies,
    technologiesDictionary,
    loadingTechnologies,
    convertProjectTechnologies,
  ]);


  // Prepare technology options for TechnologySelector
  const technologyOptions = (technologiesDictionary || []).map((tech) => ({
    id: tech.id,
    value: tech.name,
    label: tech.name,
  }));

  // Handle technology changes
  const handleTechnologiesChange = (technologies: TechnologyWithFavorite[]) => {
    setTechnologiesWithFavorites(technologies);
    const updatedProject = {
      ...localProject,
      technologies: technologies.map((tech) => ({
        id: tech.id,
        name: tech.name,
        isFavorite: tech.isFavorite,
      })),
    };
    updateLocalProject({ technologies: updatedProject.technologies });
    updateParent(updatedProject);
  };

  return (
    <Box
      mb="md"
      p="md"
      style={{
        border: `2px solid ${isEditing ? theme.colors.blue[5] : theme.colors.cyan[7]}`,
        borderRadius: theme.radius.sm,
        backgroundColor: isDark ? theme.colors.dark[6] : (isEditing ? theme.colors.blue[0] : FormUIColor.ADD_FORM_BG),
      }}
    >
      <Stack gap="md">
        {isEditing && (
          <Text size="sm" c="blue" fw={500}>
            Editing: {localProject.projectName}{localProject.customerName ? ` at ${localProject.customerName}` : ''}
          </Text>
        )}
        <Group grow>
          <Autocomplete
            label="Customer Name"
            placeholder="Client or customer name"
            value={localProject.customerName}
            onChange={(value) => {
              updateLocalProject({ customerName: value });
              updateParent({ ...localProjectRef.current, customerName: value });
            }}
            data={customersDictionary}
            limit={10}
            required
            rightSection={
              <Tooltip label="Choose from existing customers or type a new one freely" position="top" multiline maw={220} withArrow>
                <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                  <InformationCircleIcon style={{ width: rem(16) }} />
                </ActionIcon>
              </Tooltip>
            }
          />
          <TextInput
            label="Project Name"
            placeholder="Name of the project"
            value={localProject.projectName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateLocalProject({ projectName: e.target.value })
            }
            onBlur={() => updateParent(localProject)}
            required
          />
        </Group>

        <MonthYearInput
          label="Start date"
          monthValue={localProject.fromMonth}
          yearValue={localProject.fromYear}
          onMonthChange={(value: string) => {
            updateLocalProject({ fromMonth: value });
            updateParent({ ...localProject, fromMonth: value });
            // Show validation after user starts interacting with required field
            if (!value && showValidation) setShowValidation(true);
          }}
          onYearChange={(value: string) => {
            updateLocalProject({ fromYear: value });
            updateParent({ ...localProject, fromYear: value });
            // Show validation after user starts interacting with required field
            if (!value && showValidation) setShowValidation(true);
          }}
          required
          showValidation={showValidation}
        />

        <MonthYearInput
          label="End (leave blank if current)"
          monthValue={localProject.toMonth}
          yearValue={localProject.toYear}
          onMonthChange={(value: string) => {
            updateLocalProject({ toMonth: value });
            updateParent({ ...localProject, toMonth: value });
          }}
          onYearChange={(value: string) => {
            updateLocalProject({ toYear: value });
            updateParent({ ...localProject, toYear: value });
          }}
          showValidation={showValidation}
        />

        {/* Date range validation error */}
        {showValidation && validateDateRange() && (
          <Text size="sm" c="red">
            {validateDateRange()}
          </Text>
        )}

        <TagsInput
          label="Roles"
          placeholder={
            loadingRoles
              ? "Loading roles..."
              : "Type or select your roles in this project"
          }
          value={localProject.roles || []}
          onChange={(value) => {
            updateLocalProject({ roles: value });
            updateParent({ ...localProject, roles: value });
          }}
          data={rolesDictionary}
          clearable
          disabled={loadingRoles}
          limit={10}
          rightSection={
            <Tooltip label="Choose from existing role titles or type a new one and press Enter" position="top" multiline maw={220} withArrow>
              <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                <InformationCircleIcon style={{ width: rem(16) }} />
              </ActionIcon>
            </Tooltip>
          }
        />

        <TechnologySelector
          label="Skills & Technologies"
          labelTooltip="Add all relevant skills here: technologies, frameworks, tools, methodologies, soft skills, and other skills (e.g. ML, AI, Recruitment, DevOps tools)."
          placeholder={
            loadingTechnologies
              ? "Loading technologies..."
              : "Type to search and add technologies used in this project"
          }
          value={technologiesWithFavorites}
          onChange={handleTechnologiesChange}
          availableTechnologies={technologyOptions}
          disabled={loadingTechnologies}
          loading={loadingTechnologies}
          enableSuggestions={true}
          required
        />

        {showValidation && technologiesWithFavorites.length === 0 && (
          <Text size="sm" c="red">
            At least one technology is required
          </Text>
        )}

        <FormattableTextArea
          label="Description"
          placeholder="Describe the project, client needs, solution, and value..."
          minRows={5}
          maxRows={20}
          value={localProject.description ?? ""}
          onChange={(value: string) =>
            updateLocalProject({ description: value })
          }
          onBlur={() => updateParent(localProject)}
          enableImprovement={true}
          improvePrompt="Please improve this project description while maintaining its core facts and technical details:"
        />

        <FormattableTextArea
          label="Responsibilities in project"
          placeholder="Enter each responsibility on a new line..."
          value={responsibilitiesText}
          required
          minRows={5}
          maxRows={20}
          onChange={(value: string) => {
            // Only update local text immediately for responsive typing
            // Expensive updates (localProject, parent) are debounced via useEffect
            setResponsibilitiesText(value);
          }}
          enableImprovement={true}
          improvePrompt="Please improve this list of responsibilities while maintaining its core facts and technical details:"
        />

        <Group justify="flex-end" gap="sm">
          {showValidation && !isFormValid && (() => {
            const errors: string[] = [];
            if (!localProject.customerName) errors.push('Customer name is required');
            if (!localProject.projectName) errors.push('Project name is required');
            if (!localProject.fromMonth || !localProject.fromYear) errors.push('Start month and year are required');
            if (responsibilitiesText.trim() === '') errors.push('Responsibilities are required');
            if (technologiesWithFavorites.length === 0) errors.push('At least one technology is required');
            if (validateDateRange()) errors.push('End date must be after start date');
            return errors.length > 0 ? (
              <Alert color="red" w="100%" py="xs">
                <List size="sm" spacing={2}>
                  {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                </List>
              </Alert>
            ) : null;
          })()}
          <Button
            variant="outline"
            leftSection={<XMarkIcon width={16} height={16} />}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            leftSection={<CheckIcon width={16} height={16} />}
            onClick={() => {
              // Show validation errors
              setShowValidation(true);

              // Only save if form is valid
              if (isFormValid) {
                // Compute responsibilities from responsibilitiesText (always current)
                // to avoid stale localProject.responsibilities from the 300ms debounce
                const cleanedProject = {
                  ...localProject,
                  responsibilities: responsibilitiesText.split("\n").filter((r) => r.trim() !== "")
                };

                // Ensure parent has the latest cleaned state before saving
                updateParent(cleanedProject);
                onSave(cleanedProject);
              }
            }}
          >
            {isEditing ? 'Save' : 'Add Project'}
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
